import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, DownloadButton, Select } from '@devkit/ui';
import { ArrowRight } from 'lucide-react';
import { yamlToJson } from '@devkit/tools-json';
import { trackToolExecute, trackCopy, trackDownload, trackClear } from '@devkit/analytics';

const indentOptions = [
  { value: '2', label: '2 spaces' },
  { value: '4', label: '4 spaces' },
];

const defaultYaml = `name: DevKit
version: 1.0.0
private: true
scripts:
  dev: vite
  build: tsc && vite build
tags:
  - json
  - yaml
  - tools
`;

export default function YamlToJsonPage() {
  const [input, setInput] = useState(defaultYaml);
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [indent, setIndent] = useState('2');

  const handleConvert = useCallback(() => {
    setError(null);
    const result = yamlToJson(input, parseInt(indent, 10));
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to convert YAML to JSON.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('yaml-to-json', 'convert');
  }, [input, indent]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('yaml-to-json');
  }, []);

  return (
    <ToolLayout slug="yaml-to-json">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Indent:</label>
              <Select options={indentOptions} value={indent} onChange={setIndent} className="w-32" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Input YAML
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste YAML here..."
              className="w-full h-48 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={handleConvert} className="gap-2">
              <ArrowRight className="w-4 h-4" />
              Convert to JSON
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">JSON Output</label>
              {output && (
                <div className="flex items-center gap-2">
                  <CopyButton text={output} onCopy={() => trackCopy('yaml-to-json')} />
                  <DownloadButton
                    content={output}
                    filename="output.json"
                    onDownload={() => trackDownload('yaml-to-json')}
                  />
                </div>
              )}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'JSON will appear here...'}
              className={`w-full h-48 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400 font-medium">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
